import { useEffect, useState } from "react";
import { Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DOW, KINDS, TIMER_KINDS, mountainToViewer, scheduleText } from "./jobMeta";

const sel = "h-9 rounded-md border border-slate-200 bg-white px-2 text-sm";
const num = (v) => (v === "" || v === undefined ? undefined : Number(v));

function TimeFields({ s, set }) {
  return (
    <div className="flex items-center gap-1.5">
      <Input type="number" min={0} max={23} value={s.hour ?? ""} onChange={(e) => set({ hour: num(e.target.value) })} className="h-9 w-16 font-mono" data-testid="job-edit-hour" />
      <span>:</span>
      <Input type="number" min={0} max={59} value={s.minute ?? ""} onChange={(e) => set({ minute: num(e.target.value) })} className="h-9 w-16 font-mono" data-testid="job-edit-minute" />
    </div>
  );
}

function ScheduleFields({ s, set, timer }) {
  const k = s.kind;
  return (
    <div className="flex flex-wrap items-center gap-3 text-sm text-slate-700">
      <select value={k} onChange={(e) => set({ kind: e.target.value, dow: s.dow ?? 0, day: s.day ?? 1, hour: s.hour ?? 8, minute: s.minute ?? 0, minutes: s.minutes ?? 60 })} className={sel} data-testid="job-edit-kind">
        {(timer ? TIMER_KINDS : KINDS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
      {k === "weekly" && <select value={s.dow ?? 0} onChange={(e) => set({ dow: Number(e.target.value) })} className={sel} data-testid="job-edit-dow">
        {DOW.map((d, i) => <option key={d} value={i}>{d}</option>)}</select>}
      {k === "monthly" && <label className="flex items-center gap-1.5">day <Input type="number" min={1} max={28} value={s.day ?? ""} onChange={(e) => set({ day: num(e.target.value) })} className="h-9 w-16 font-mono" data-testid="job-edit-day" /></label>}
      {["daily", "weekdays", "weekly", "monthly"].includes(k) && <label className="flex items-center gap-1.5">at <TimeFields s={s} set={set} /></label>}
      {k === "hourly" && <label className="flex items-center gap-1.5">at minute <Input type="number" min={0} max={59} value={s.minute ?? ""} onChange={(e) => set({ minute: num(e.target.value) })} className="h-9 w-16 font-mono" data-testid="job-edit-minute" /></label>}
      {(k === "gap" || k === "interval") && <label className="flex items-center gap-1.5">every <Input type="number" min={timer ? 1 : 10} value={s.minutes ?? ""} onChange={(e) => set({ minutes: num(e.target.value) })} className="h-9 w-24 font-mono" data-testid="job-edit-minutes" /> minutes</label>}
    </div>
  );
}

/** Edit one job's schedule and note. `job` = null when closed. */
export function JobEditDialog({ job, tz, onClose, onSaved }) {
  const [s, setS] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (job) { setS({ ...job.schedule }); setNotes(job.notes || ""); } }, [job]);
  if (!job || !s) return null;
  const canSchedule = job.type !== "platform" && !job.locked;
  const call = async (body, msg) => {
    setBusy(true);
    try { await api.put(`/cron/jobs/${encodeURIComponent(job.key)}`, body); toast.success(msg); onSaved(); onClose(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not save"); } finally { setBusy(false); }
  };
  const changed = JSON.stringify(s) !== JSON.stringify(job.schedule);
  const noteBody = notes.trim() === (job.notes_default || "").trim() ? (job.notes_custom ? { reset_notes: true } : {}) : { notes };
  const body = { ...(canSchedule && changed ? { schedule: s } : {}), ...noteBody };
  const save = () => (Object.keys(body).length ? call(body, `${job.label} saved`) : onClose());
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="job-edit-dialog">
        <DialogHeader><DialogTitle>{job.label}</DialogTitle>
          <DialogDescription className="font-mono text-[11px]">{job.key} · {job.fn || job.endpoint}</DialogDescription></DialogHeader>
        <div className="space-y-4">
          {canSchedule ? (
            <div className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">When it runs <span className="font-normal normal-case">({job.tz || tz} time; checked every 10 minutes)</span></div>
              <ScheduleFields s={s} set={(p) => setS({ ...s, ...p })} timer={job.type === "timer"} />
              <p className="text-[11px] text-slate-500" data-testid="job-edit-preview">{scheduleText(s)}{job.tz ? ` ${job.tz}` : ""}{!job.tz && s.hour != null && ["daily", "weekdays", "weekly", "monthly"].includes(s.kind) && mountainToViewer(s.hour, s.minute) ? ` (${mountainToViewer(s.hour, s.minute)} your time)` : ""}. Changing it never triggers a catch-up run — the next run is the next matching time.</p>
              {job.schedule_custom && <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" disabled={busy} onClick={() => call({ reset_schedule: true }, "Back to the built-in schedule")} data-testid="job-edit-reset-schedule"><RotateCcw className="h-3 w-3" /> Use the built-in schedule ({scheduleText(job.default_schedule)})</Button>}
            </div>
          ) : <p className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600">{job.type === "platform" ? "The platform trigger's schedule is part of the deployment and changes only by redeploying." : "This timer drives every scheduled job, so its schedule can't be changed."}</p>}
          <div className="space-y-1.5">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Notes</div>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} maxLength={1000} className="text-sm" data-testid="job-edit-notes" />
            {notes !== job.notes_default && <button type="button" onClick={() => setNotes(job.notes_default)} className="text-[11px] font-medium text-emerald-700 hover:underline" data-testid="job-edit-default-notes">Use the default note</button>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="job-edit-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="job-edit-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
